import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { Button } from "@/components/flexkit/Button";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { useCallback, type FC } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export const BackButton: FC<PropsWithClassName<{ showLabel?: boolean }>> = ({ className, showLabel = true }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { environmentId } = useParams();

    const isTaskRunsPage = location.pathname.includes("/tasks");
    const isTaskRunLogPage = location.pathname.includes("/task/");
    const isServiceLogsPage = location.pathname.includes("/services/");
    const isEnvironmentLogsPage = location.pathname.endsWith(`${environmentId}/logs`);

    const onClick = useCallback(() => {
        if (!environmentId) {
            return;
        }
        if (!isTaskRunsPage && !isServiceLogsPage && !isEnvironmentLogsPage && !isTaskRunLogPage) {
            return;
        }

        if (isTaskRunLogPage) {
            navigate(`/details/${environmentId}/tasks`);
            return;
        }

        navigate(`/details/${environmentId}`);
    }, [environmentId, isEnvironmentLogsPage, isServiceLogsPage, isTaskRunLogPage, isTaskRunsPage, navigate]);

    useEscapeShortcut(
        useCallback(() => {
            if (!environmentId) {
                return;
            }

            navigate(`/details/${environmentId}`);
        }, [environmentId, navigate]),
    );

    return (
        <Tooltip
            content={
                <div className="flex flex-row items-center gap-2">
                    Back to environment
                    <KeyboardKey className="bg-surface-invert-tertiary">Esc</KeyboardKey>
                </div>
            }
        >
            <Button
                disabled={!environmentId}
                variant={"ghost"}
                className={cn("border-transparent pl-2 text-lg font-bold", className)}
                LeadingIcon={IconArrowLeft}
                onClick={onClick}
                aria-label="Back"
                data-tracking-id="back-button-environment-details"
            >
                {showLabel && <span>Back</span>}
            </Button>
        </Tooltip>
    );
};
